import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Edit,
  PersonOutline,
  BadgeOutlined,
  ContactPageOutlined,
  LocationOnOutlined,
  CakeOutlined,
  WorkOutlineOutlined,
  PhoneOutlined,
  EmailOutlined,
  WhatsApp,
  ContactPhoneOutlined,
  FamilyRestroomOutlined,
  SportsKabaddiOutlined,
  CreditCardOutlined,
  StorefrontOutlined,
  FingerprintOutlined,
  QrCode2,
} from "@mui/icons-material";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Header from "../../components/Header";
import FamilyMemberSelector from "../../components/FamilyMemberSelector";
import MemberQrCode from "../../components/MemberQrCode";
import MemberStatusChip, { FreeCardChip } from "../../components/MemberStatusChip";
import { AuthContext } from "../../components/AuthContext";
import config from "../../config";
import { getAuthToken } from "../../utils/authStorage";
import {
  isFirebaseStorageEnabled,
  resolveProfileImageUrl,
  uploadProfileImageToFirebase,
} from "../../utils/profileImageStorage";
import { tokens } from "../../theme";

const ViewMember = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const navigate = useNavigate();
  const { role, memberId, familyMembers, activeMember, selectedMemberId, selectFamilyMember } =
    useContext(AuthContext);

  const locationMember = location.state?.member || null;
  const [availableMembers, setAvailableMembers] = useState(
    familyMembers.length > 0 ? familyMembers : locationMember ? [locationMember] : []
  );
  const [currentMemberId, setCurrentMemberId] = useState(
    selectedMemberId || locationMember?.member_id || activeMember?.member_id || null
  );
  const [memberProfile, setMemberProfile] = useState(locationMember || activeMember || null);
  const [attendanceOverview, setAttendanceOverview] = useState({
    attendance_count: 0,
    discipline_counts: [],
    recent_attendance: [],
    attendance_history: [],
  });
  const [loading, setLoading] = useState(false);
  const [profilePic, setProfilePic] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({
    nic: "",
    gender: "",
    studentDob: "",
    occupation: "",
    mobilePhone: "",
    residencePhone: "",
    whatsappNumber: "",
    homeAddress: "",
    city: "",
    country: "",
    emergencyContactPersonName: "",
    emergencyContactNumber: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const canChangeStatus = role === "global_admin" || role === "branch_admin";
  const isInactive = memberProfile?.status === "inactive";

  const canEditPhoto =
    role === "global_admin" ||
    role === "branch_admin" ||
    (role === "member" &&
      (String(memberProfile?.member_id) === String(memberId) ||
        familyMembers.some(
          (fm) => String(fm.member_id) === String(memberProfile?.member_id)
        )));

  const canEditProfileFields =
    role === "member" &&
    (String(memberProfile?.member_id) === String(memberId) ||
      familyMembers.some(
        (fm) => String(fm.member_id) === String(memberProfile?.member_id)
      ));

  const getAuthorizedRequestConfig = () => {
    const token = getAuthToken();
    return token
      ? {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
      : {};
  };

  useEffect(() => {
    if (familyMembers.length > 0) {
      setAvailableMembers(familyMembers);
    }
  }, [familyMembers]);

  useEffect(() => {
    if (!currentMemberId) {
      const fallbackId =
        selectedMemberId ||
        activeMember?.member_id ||
        locationMember?.member_id ||
        availableMembers[0]?.member_id ||
        null;

      if (fallbackId) {
        setCurrentMemberId(fallbackId);
      }
    }
  }, [activeMember, availableMembers, currentMemberId, locationMember, selectedMemberId]);

  const selectedMember = useMemo(
    () =>
      availableMembers.find(
        (member) => String(member.member_id) === String(currentMemberId)
      ) || memberProfile || activeMember || locationMember || null,
    [activeMember, availableMembers, currentMemberId, locationMember, memberProfile]
  );

  useEffect(() => {
    if (!selectedMember?.membership_id) {
      return;
    }

    const fetchMemberData = async () => {
      const requestConfig = getAuthorizedRequestConfig();

      try {
        setLoading(true);
        const [profileResult, attendanceResult] = await Promise.allSettled([
          axios.get(
            `${config.apiBaseUrl}/member_profile/${selectedMember.membership_id}`,
            requestConfig
          ),
          axios.post(
            `${config.apiBaseUrl}/member_attendance_overview`,
            {
              membership_id: selectedMember.membership_id,
            },
            requestConfig
          ),
        ]);

        if (profileResult.status === "fulfilled") {
          setMemberProfile(profileResult.value.data.member);
          if (Array.isArray(profileResult.value.data.family_members)) {
            setAvailableMembers(profileResult.value.data.family_members);
          }
        } else {
          const message =
            profileResult.reason?.response?.data?.error || "Failed to load member profile";
          toast.error(message);
        }

        if (attendanceResult.status === "fulfilled") {
          setAttendanceOverview(attendanceResult.value.data);
        } else {
          console.error("Failed to load member attendance overview:", attendanceResult.reason);
          setAttendanceOverview({
            attendance_count: 0,
            discipline_counts: [],
            recent_attendance: [],
            attendance_history: [],
          });
          toast.error(
            attendanceResult.reason?.response?.data?.error || "Failed to load attendance records"
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMemberData();
  }, [selectedMember?.membership_id]);

  useEffect(() => {
    let isMounted = true;

    const loadProfileImage = async () => {
      const nextPic = await resolveProfileImageUrl(memberProfile?.profile_image);
      if (isMounted) {
        setProfilePic(nextPic);
      }
    };

    loadProfileImage();

    return () => {
      isMounted = false;
    };
  }, [memberProfile]);

  const handleMemberSelection = (memberId) => {
    setCurrentMemberId(memberId);
    if (role === "member") {
      selectFamilyMember(memberId);
    }
  };

  const handlePhotoChange = async (file) => {
    if (file && memberProfile?.membership_id) {
      const success = await uploadProfileImage(memberProfile.membership_id, file);
      if (success) {
        toast.success("Profile image updated successfully");
        setProfilePic(await resolveProfileImageUrl(success));
      } else {
        toast.error("Failed to update profile image");
      }
    }
  };

  const uploadProfileImage = async (membershipId, file) => {
    if (!canEditPhoto) {
      toast.error("You are not allowed to update this profile image");
      return false;
    }

    if (!file) {
      return false;
    }

    const profileImagePath = await handleFileUpload(membershipId, file);
    if (!profileImagePath) {
      return false;
    }

    try {
      await axios.put(
        `${config.apiBaseUrl}/set_profile_image/${membershipId}`,
        {
          profileImage: profileImagePath,
        },
        getAuthorizedRequestConfig()
      );
      return profileImagePath;
    } catch (error) {
      toast.error("Failed to set profile image");
      return false;
    }
  };

  const handleFileUpload = async (membershipId, file) => {
    if (!file) {
      toast.error("No file selected");
      return null;
    }

    if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
      toast.error("Invalid file type. Only JPEG, JPG, and PNG are allowed.");
      return null;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size exceeds 5MB");
      return null;
    }

    try {
      return await uploadProfileImageToFirebase(membershipId, file);
    } catch (error) {
      const defaultMessage = isFirebaseStorageEnabled
        ? "Failed to upload file to Firebase Storage"
        : "Firebase Storage is not configured";
      toast.error(error?.message || defaultMessage);
      return null;
    }
  };

  const handleStatusChange = async () => {
    if (!memberProfile?.member_id) return;
    const target = isInactive ? "active" : "inactive";
    try {
      setSavingStatus(true);
      const response = await axios.put(
        `${config.apiBaseUrl}/member_status/${memberProfile.member_id}`,
        { status: target },
        getAuthorizedRequestConfig()
      );
      const nextStatus = response.data.status;
      setMemberProfile((current) => (current ? { ...current, status: nextStatus } : current));
      setAvailableMembers((members) =>
        members.map((member) =>
          String(member.member_id) === String(memberProfile.member_id)
            ? { ...member, status: nextStatus }
            : member
        )
      );
      setStatusDialogOpen(false);
      toast.success(target === "inactive" ? "Member marked inactive" : "Member reactivated");
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to update member status");
    } finally {
      setSavingStatus(false);
    }
  };

  const openProfileEditDialog = () => {
    setProfileForm({
      nic: memberProfile?.nic || "",
      gender: memberProfile?.gender || "",
      studentDob: memberProfile?.student_dob || memberProfile?.dob || "",
      occupation: memberProfile?.occupation || "",
      mobilePhone: memberProfile?.mobile_phone || "",
      residencePhone: memberProfile?.residence_phone || "",
      whatsappNumber: memberProfile?.whatsapp_number || "",
      homeAddress: memberProfile?.home_address || "",
      city: memberProfile?.city || "",
      country: memberProfile?.country || "",
      emergencyContactPersonName: memberProfile?.emergency_contact_person_name || "",
      emergencyContactNumber: memberProfile?.emergency_contact_number || "",
    });
    setEditDialogOpen(true);
  };

  const handleProfileFieldChange = (field, value) => {
    setProfileForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveProfileFields = async () => {
    try {
      setSavingProfile(true);
      await axios.put(`${config.apiBaseUrl}/update_member_self`, { ...profileForm, targetMemberId: memberProfile?.member_id }, getAuthorizedRequestConfig());
      setMemberProfile((prev) => ({
        ...prev,
        nic: profileForm.nic,
        gender: profileForm.gender,
        student_dob: profileForm.studentDob,
        dob: profileForm.studentDob,
        occupation: profileForm.occupation,
        mobile_phone: profileForm.mobilePhone,
        residence_phone: profileForm.residencePhone,
        whatsapp_number: profileForm.whatsappNumber,
        home_address: profileForm.homeAddress,
        city: profileForm.city,
        country: profileForm.country,
        emergency_contact_person_name: profileForm.emergencyContactPersonName,
        emergency_contact_number: profileForm.emergencyContactNumber,
      }));
      setEditDialogOpen(false);
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const InfoRow = ({ icon, label, value }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 1.5,
        transition: "all 0.3s ease",
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.05)",
          borderRadius: "8px",
          px: 1,
        },
      }}
    >
      <Box
        sx={{
          color: "#3498db",
          display: "flex",
          alignItems: "center",
          minWidth: "24px",
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: "#95a5a6",
            fontSize: "0.75rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "#ecf0f1",
            fontSize: "0.95rem",
            fontWeight: 500,
            wordBreak: "break-word",
          }}
        >
          {value ?? "N/A"}
        </Typography>
      </Box>
    </Box>
  );

  const SectionTitle = ({ title }) => (
    <Box sx={{ mb: 2, mt: 3 }}>
      <Typography
        variant="h6"
        sx={{
          color: "#3498db",
          fontWeight: 600,
          fontSize: "1.1rem",
          letterSpacing: "0.5px",
        }}
      >
        {title}
      </Typography>
      <Divider
        sx={{
          mt: 1,
          borderColor: "rgba(52, 152, 219, 0.3)",
          borderWidth: "1px",
        }}
      />
    </Box>
  );

  const formattedDob = memberProfile?.student_dob || memberProfile?.dob;
  const attendanceHistory = attendanceOverview.attendance_history || [];
  const attendanceColumns = [
    {
      field: "attendance_date",
      headerName: "Attendance Date",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "discipline_name",
      headerName: "Discipline",
      flex: 1,
      minWidth: 180,
      renderCell: ({ row }) => row.discipline_name || "Unassigned",
    },
  ];

  return (
    <>
      <Box m={{ xs: "12px", sm: "16px", md: "20px" }}>
        <Header title="Member Profile" subtitle="View Member Details" />
        <ToastContainer />
        {!selectedMember && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No member profile is available for this session.
          </Alert>
        )}
        {selectedMember && (
          <FamilyMemberSelector
            members={availableMembers}
            selectedMemberId={currentMemberId || selectedMember.member_id}
            onChange={handleMemberSelection}
            label="Profile Member"
          />
        )}
        <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 2.5, sm: 3, md: 4 } }}>
          <Card
            sx={{
              maxWidth: { xs: "100%", md: 980, lg: 1080 },
              width: "100%",
              background: "linear-gradient(145deg, #1a252f 0%, #2c3e50 100%)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
              borderRadius: "24px",
              overflow: "hidden",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #3498db, #2ecc71, #e74c3c, #f39c12)",
              },
            }}
          >
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                pt: { xs: 3, sm: 4 },
                pb: 2,
                px: { xs: 2, sm: 4 },
                textAlign: "center",
                position: "relative",
              }}
            >
              <Box position="relative" display="inline-block">
                <Avatar
                  src={profilePic}
                  sx={{
                    width: { xs: 108, sm: 140 },
                    height: { xs: 108, sm: 140 },
                    border: "5px solid rgba(255, 255, 255, 0.9)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                  }}
                />
                {canEditPhoto && (
                  <Tooltip title="Change Photo" arrow>
                    <IconButton
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        backgroundColor: "#3498db",
                        color: "white",
                        width: 40,
                        height: 40,
                        boxShadow: "0 4px 12px rgba(52, 152, 219, 0.4)",
                      }}
                      component="label"
                    >
                      <Edit fontSize="small" />
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        hidden
                        onChange={(event) => handlePhotoChange(event.target.files[0])}
                      />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Typography variant="h3" sx={{ color: "white", fontWeight: 700, mt: 2, fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" } }}>
                {memberProfile
                  ? `${memberProfile.first_name} ${memberProfile.last_name}`
                  : "Member"}
              </Typography>
              <Chip
                label={`ID: ${memberProfile?.membership_id || "N/A"}`}
                sx={{
                  mt: 1,
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  color: "white",
                  fontWeight: 600,
                  fontSize: { xs: "0.78rem", sm: "0.9rem" },
                }}
              />
              {memberProfile?.status && (
                <Box mt={1}>
                  <MemberStatusChip status={memberProfile.status} size="medium" />
                </Box>
              )}
              {/* Staff only: members never see the free card flag (the API leaves it out for them too). */}
              {canChangeStatus && Number(memberProfile?.is_free_card) === 1 && (
                <Box mt={1}>
                  <FreeCardChip size="medium" sx={{ color: "white", borderColor: "rgba(255, 255, 255, 0.8)" }} />
                </Box>
              )}
              {canChangeStatus && memberProfile?.status && (
                <Box mt={2}>
                  <Button
                    variant="contained"
                    color={isInactive ? "success" : "error"}
                    onClick={() => setStatusDialogOpen(true)}
                  >
                    {isInactive ? "Reactivate" : "Mark inactive"}
                  </Button>
                </Box>
              )}
              {canEditProfileFields && (
                <Box mt={2}>
                  <Button variant="contained" color="secondary" onClick={openProfileEditDialog}>
                    Edit My Profile
                  </Button>
                </Box>
              )}
              <Box mt={2}>
                <Button
                  variant="outlined"
                  startIcon={<QrCode2 />}
                  onClick={() => setQrDialogOpen(true)}
                  sx={{
                    color: "white",
                    borderColor: "rgba(255, 255, 255, 0.65)",
                    "&:hover": {
                      borderColor: "white",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  Show Attendance QR
                </Button>
              </Box>
            </Box>

            <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 }, color: "white" }}>
              <SectionTitle title="Personal Information" />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InfoRow icon={<ContactPageOutlined />} label="NIC" value={memberProfile?.nic} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoRow icon={<PersonOutline />} label="Gender" value={memberProfile?.gender} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoRow
                    icon={<CakeOutlined />}
                    label="Date of Birth"
                    value={
                      formattedDob
                        ? new Date(formattedDob).toLocaleDateString("en-US", {
                          weekday: "short",
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoRow icon={<WorkOutlineOutlined />} label="Occupation" value={memberProfile?.occupation} />
                </Grid>
              </Grid>

              <SectionTitle title="Contact Information" />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InfoRow icon={<PhoneOutlined />} label="Mobile Phone" value={memberProfile?.mobile_phone} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoRow icon={<PhoneOutlined />} label="Residence Phone" value={memberProfile?.residence_phone} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoRow icon={<EmailOutlined />} label="Email" value={memberProfile?.email} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoRow icon={<WhatsApp />} label="WhatsApp" value={memberProfile?.whatsapp_number} />
                </Grid>
              </Grid>

              <SectionTitle title="Address" />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <InfoRow icon={<LocationOnOutlined />} label="Home Address" value={memberProfile?.home_address} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoRow icon={<LocationOnOutlined />} label="City" value={memberProfile?.city} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoRow icon={<LocationOnOutlined />} label="Country" value={memberProfile?.country} />
                </Grid>
              </Grid>

              <SectionTitle title="Membership Details" />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InfoRow icon={<StorefrontOutlined />} label="Branch" value={memberProfile?.branch_name} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoRow
                    icon={<SportsKabaddiOutlined />}
                    label="Primary Discipline"
                    value={memberProfile?.discipline_name_one}
                  />
                </Grid>
                {memberProfile?.discipline_name_two && (
                  <Grid item xs={12} md={6}>
                    <InfoRow
                      icon={<SportsKabaddiOutlined />}
                      label="Secondary Discipline"
                      value={memberProfile.discipline_name_two}
                    />
                  </Grid>
                )}
                {memberProfile?.discipline_name_three && (
                  <Grid item xs={12} md={6}>
                    <InfoRow
                      icon={<SportsKabaddiOutlined />}
                      label="Third Discipline"
                      value={memberProfile.discipline_name_three}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <InfoRow
                    icon={<CreditCardOutlined />}
                    label="Subscription"
                    value={memberProfile?.subscription_name}
                  />
                </Grid>
                {role !== "member" && (
                  <Grid item xs={12} md={6}>
                    <InfoRow icon={<FingerprintOutlined />} label="RFID" value={memberProfile?.rfid} />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <InfoRow icon={<FamilyRestroomOutlined />} label="Family ID" value={memberProfile?.family_id} />
                </Grid>
              </Grid>

              <SectionTitle title="Attendance Overview" />
              {loading ? (
                <Typography variant="body2">Loading attendance overview...</Typography>
              ) : (
                <Box>
                  <Paper
                    elevation={0}
                    sx={{
                      mb: 3,
                      p: 3,
                      borderRadius: "18px",
                      backgroundColor: colors.primary[400],
                      border: `1px solid ${colors.blueAccent[700]}`,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            height: "100%",
                            p: 2.25,
                            borderRadius: "16px",
                            background: "linear-gradient(135deg, rgba(52, 152, 219, 0.18), rgba(46, 204, 113, 0.12))",
                            border: "1px solid rgba(52, 152, 219, 0.28)",
                          }}
                        >
                          <InfoRow
                            icon={<BadgeOutlined />}
                            label="Total Attendance"
                            value={attendanceOverview.attendance_count ?? 0}
                          />
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Paper
                          elevation={0}
                          sx={{
                            height: "100%",
                            p: 2.25,
                            borderRadius: "16px",
                            backgroundColor: "rgba(255,255,255,0.03)",
                            border: "1px solid rgba(255,255,255,0.08)",
                          }}
                        >
                          <Typography
                            variant="overline"
                            sx={{ color: "#95a5a6", letterSpacing: "0.14em", fontWeight: 700 }}
                          >
                            Attendance by Discipline
                          </Typography>
                          <Box mt={1.5} display="flex" gap={1} flexWrap="wrap">
                            {(attendanceOverview.discipline_counts || []).map((entry) => (
                              <Chip
                                key={`${entry.discipline_name}-${entry.total}`}
                                label={`${entry.discipline_name || "Unassigned"}: ${entry.total}`}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  backgroundColor: "rgba(52, 152, 219, 0.14)",
                                  color: "#ecf0f1",
                                  border: "1px solid rgba(52, 152, 219, 0.28)",
                                }}
                              />
                            ))}
                            {(!attendanceOverview.discipline_counts || attendanceOverview.discipline_counts.length === 0) && (
                              <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                                No attendance records yet.
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "18px",
                      backgroundColor: colors.primary[400],
                      border: `1px solid ${colors.blueAccent[700]}`,
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
                      <Box>
                        <Typography variant="h5" sx={{ color: colors.greenAccent[400], fontWeight: 700 }}>
                          Attendance History
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                          Latest attendance records for this member.
                        </Typography>
                      </Box>
                      <Chip
                        label={`${attendanceHistory.length} record${attendanceHistory.length === 1 ? "" : "s"}`}
                        sx={{
                          backgroundColor: "rgba(46, 204, 113, 0.16)",
                          color: "#ecf0f1",
                          fontWeight: 700,
                        }}
                      />
                    </Box>

                    {attendanceHistory.length > 0 ? (
                      <Box
                        sx={{
                          height: { xs: 300, sm: 340, md: 360 },
                          overflowX: "auto",
                          "& .MuiDataGrid-root": {
                            border: "none",
                            backgroundColor: "transparent",
                          },
                          "& .MuiDataGrid-cell": {
                            borderBottom: "1px solid rgba(255,255,255,0.06)",
                            fontSize: { xs: "0.78rem", sm: "0.84rem", md: "0.875rem" },
                          },
                          "& .MuiDataGrid-columnHeaderTitle": {
                            fontSize: { xs: "0.74rem", sm: "0.8rem", md: "0.875rem" },
                            fontWeight: 700,
                          },
                          "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: colors.blueAccent[700],
                            borderBottom: "none",
                          },
                          "& .MuiDataGrid-footerContainer": {
                            borderTop: "none",
                            backgroundColor: colors.blueAccent[700],
                          },
                          "& .MuiDataGrid-virtualScroller": {
                            backgroundColor: colors.primary[500],
                          },
                        }}
                      >
                        <DataGrid
                          rows={attendanceHistory.map((entry, index) => ({
                            id: `${entry.attendance_date}-${entry.discipline_name || "none"}-${index}`,
                            ...entry,
                          }))}
                          columns={attendanceColumns}
                          rowHeight={isMobile ? 46 : 52}
                          disableRowSelectionOnClick
                          pageSizeOptions={[10, 25, 50]}
                          initialState={{
                            pagination: {
                              paginationModel: { pageSize: 10, page: 0 },
                            },
                          }}
                        />
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        No attendance history found for this member yet.
                      </Alert>
                    )}
                  </Paper>
                </Box>
              )}

              <SectionTitle title="Emergency Contact" />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InfoRow
                    icon={<ContactPhoneOutlined />}
                    label="Contact Person"
                    value={memberProfile?.emergency_contact_person_name}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoRow
                    icon={<PhoneOutlined />}
                    label="Contact Number"
                    value={memberProfile?.emergency_contact_number}
                  />
                </Grid>
              </Grid>

              <Box mt={4} display="flex" gap={2} flexWrap="wrap">
                {role === "global_admin" || role === "branch_admin" ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => navigate("/teba/payment-receive", { state: { membershipId: memberProfile?.membership_id } })}
                    size={isMobile ? "small" : "medium"}
                  >
                    Receive Payment
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => navigate("/member_payment", { state: { member: memberProfile } })}
                    size={isMobile ? "small" : "medium"}
                  >
                    View Payment History
                  </Button>
                )}
                <Button variant="outlined" onClick={() => navigate("/member_list")} size={isMobile ? "small" : "medium"}>
                  Back to Member List
                </Button>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  mt: 4,
                  p: 2,
                  backgroundColor: "rgba(52, 152, 219, 0.1)",
                  borderRadius: "12px",
                  border: "1px solid rgba(52, 152, 219, 0.2)",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "#7f8c8d", fontStyle: "italic", fontSize: "0.9rem" }}
                >
                  Keep your family profile details up to date.
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Box>
      </Box>
      <Dialog
        open={statusDialogOpen}
        onClose={() => !savingStatus && setStatusDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{isInactive ? "Reactivate member?" : "Mark member inactive?"}</DialogTitle>
        <DialogContent>
          <Typography>
            {isInactive
              ? `${memberProfile?.first_name || "This member"} will go back to Active, or Payment Pending if the family's payment is overdue.`
              : `${memberProfile?.first_name || "This member"} will be shown as Inactive until reactivated.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)} disabled={savingStatus}>Cancel</Button>
          <Button
            variant="contained"
            color={isInactive ? "success" : "error"}
            onClick={handleStatusChange}
            disabled={savingStatus}
          >
            {isInactive ? "Reactivate" : "Mark inactive"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Attendance QR Code</DialogTitle>
        <DialogContent>
          <MemberQrCode membershipId={memberProfile?.membership_id} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Edit My Profile</DialogTitle>
        <DialogContent>
          <Box
            display="grid"
            gap="16px"
            mt="4px"
            gridTemplateColumns={{ xs: "repeat(1, minmax(0, 1fr))", sm: "repeat(2, minmax(0, 1fr))" }}
            sx={{
              "& > div": { gridColumn: { xs: "span 1", sm: undefined } },
            }}
          >
            <TextField
              label="NIC"
              value={profileForm.nic}
              onChange={(event) => handleProfileFieldChange("nic", event.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="member-gender-label">Gender</InputLabel>
              <Select
                labelId="member-gender-label"
                value={profileForm.gender}
                label="Gender"
                onChange={(event) => handleProfileFieldChange("gender", event.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Date of Birth"
              type="date"
              value={profileForm.studentDob ? String(profileForm.studentDob).slice(0, 10) : ""}
              onChange={(event) => handleProfileFieldChange("studentDob", event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Occupation"
              value={profileForm.occupation}
              onChange={(event) => handleProfileFieldChange("occupation", event.target.value)}
              fullWidth
            />
            <TextField
              label="Mobile Phone"
              value={profileForm.mobilePhone}
              onChange={(event) => handleProfileFieldChange("mobilePhone", event.target.value)}
              fullWidth
            />
            <TextField
              label="Residence Phone"
              value={profileForm.residencePhone}
              onChange={(event) => handleProfileFieldChange("residencePhone", event.target.value)}
              fullWidth
            />
            <TextField
              label="WhatsApp"
              value={profileForm.whatsappNumber}
              onChange={(event) => handleProfileFieldChange("whatsappNumber", event.target.value)}
              fullWidth
            />
            <TextField
              label="Home Address"
              value={profileForm.homeAddress}
              onChange={(event) => handleProfileFieldChange("homeAddress", event.target.value)}
              fullWidth
            />
            <TextField
              label="City"
              value={profileForm.city}
              onChange={(event) => handleProfileFieldChange("city", event.target.value)}
              fullWidth
            />
            <TextField
              label="Country"
              value={profileForm.country}
              onChange={(event) => handleProfileFieldChange("country", event.target.value)}
              fullWidth
            />
            <TextField
              label="Emergency Contact Person"
              value={profileForm.emergencyContactPersonName}
              onChange={(event) => handleProfileFieldChange("emergencyContactPersonName", event.target.value)}
              fullWidth
            />
            <TextField
              label="Emergency Contact Number"
              value={profileForm.emergencyContactNumber}
              onChange={(event) => handleProfileFieldChange("emergencyContactNumber", event.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={savingProfile}>Cancel</Button>
          <Button onClick={handleSaveProfileFields} variant="contained" disabled={savingProfile}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ViewMember;
